import React, { useState } from 'react';
import { Video, Key, Users, Sliders, Monitor } from 'lucide-react';

interface ZoomSettings {
  apiKey: string;
  apiSecret: string;
  defaultSettings: {
    muteOnEntry: boolean;
    waitingRoom: boolean;
    requirePassword: boolean;
    autoRecord: boolean;
  };
  preferences: {
    defaultLength: number;
    defaultView: 'gallery' | 'speaker';
    showParticipantNames: boolean;
    enableVirtualBackground: boolean;
  };
  security: {
    enforceEncryption: boolean;
    requireAuthentication: boolean;
    blockScreenshareForGuests: boolean;
  };
}

export default function ZoomSettings() {
  const [settings, setSettings] = useState<ZoomSettings>({
    apiKey: '',
    apiSecret: '',
    defaultSettings: {
      muteOnEntry: true,
      waitingRoom: true,
      requirePassword: true,
      autoRecord: false
    },
    preferences: {
      defaultLength: 60,
      defaultView: 'gallery',
      showParticipantNames: true,
      enableVirtualBackground: true
    },
    security: {
      enforceEncryption: true,
      requireAuthentication: true,
      blockScreenshareForGuests: true
    }
  });

  const handleDefaultSettingChange = (key: keyof ZoomSettings['defaultSettings']) => {
    setSettings({
      ...settings,
      defaultSettings: {
        ...settings.defaultSettings,
        [key]: !settings.defaultSettings[key]
      }
    });
  };

  const handleSecurityChange = (key: keyof ZoomSettings['security']) => {
    setSettings({
      ...settings,
      security: {
        ...settings.security,
        [key]: !settings.security[key]
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* API Configuration */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Key className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">API Configuration</h3>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">API Key</label>
            <input
              type="password"
              value={settings.apiKey}
              onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">API Secret</label>
            <input
              type="password"
              value={settings.apiSecret}
              onChange={(e) => setSettings({ ...settings, apiSecret: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Default Meeting Settings */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Sliders className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Default Meeting Settings</h3>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Mute participants upon entry</span>
            <button
              onClick={() => handleDefaultSettingChange('muteOnEntry')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.defaultSettings.muteOnEntry ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.defaultSettings.muteOnEntry ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Enable waiting room</span>
            <button
              onClick={() => handleDefaultSettingChange('waitingRoom')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.defaultSettings.waitingRoom ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.defaultSettings.waitingRoom ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Require meeting password</span>
            <button
              onClick={() => handleDefaultSettingChange('requirePassword')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.defaultSettings.requirePassword ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.defaultSettings.requirePassword ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Automatically record meetings</span>
            <button
              onClick={() => handleDefaultSettingChange('autoRecord')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.defaultSettings.autoRecord ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.defaultSettings.autoRecord ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Meeting Preferences */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Monitor className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Meeting Preferences</h3>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Default Meeting Length</label>
            <select
              value={settings.preferences.defaultLength}
              onChange={(e) => setSettings({
                ...settings,
                preferences: {
                  ...settings.preferences,
                  defaultLength: Number(e.target.value)
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Default View</label>
            <select
              value={settings.preferences.defaultView}
              onChange={(e) => setSettings({
                ...settings,
                preferences: {
                  ...settings.preferences,
                  defaultView: e.target.value as 'gallery' | 'speaker'
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="gallery">Gallery View</option>
              <option value="speaker">Speaker View</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Show participant names</span>
            <button
              onClick={() => setSettings({
                ...settings,
                preferences: {
                  ...settings.preferences,
                  showParticipantNames: !settings.preferences.showParticipantNames
                }
              })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.preferences.showParticipantNames ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.preferences.showParticipantNames ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <Users className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Security Settings</h3>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Enforce End-to-End Encryption</span>
              <p className="text-sm text-gray-500">Require encryption for all meetings</p>
            </div>
            <button
              onClick={() => handleSecurityChange('enforceEncryption')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.security.enforceEncryption ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.security.enforceEncryption ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Require Authentication</span>
              <p className="text-sm text-gray-500">Only authenticated users can join meetings</p>
            </div>
            <button
              onClick={() => handleSecurityChange('requireAuthentication')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.security.requireAuthentication ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.security.requireAuthentication ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Block Guest Screen Sharing</span>
              <p className="text-sm text-gray-500">Only hosts can share their screen</p>
            </div>
            <button
              onClick={() => handleSecurityChange('blockScreenshareForGuests')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.security.blockScreenshareForGuests ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                  settings.security.blockScreenshareForGuests ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}